import { moneyStrings } from '@data/money';
import { MobxZodField, unwrapZodType } from '@monoid-dev/mobx-zod-form';
import { DATE_REGEX, regexOrEmpty } from '@utils/types';
import dayjs from 'dayjs';
import { z, type ZodTypeAny } from 'zod';

declare module '@monoid-dev/mobx-zod-form' {
  export interface FormMeta {
    /**
     * @param rawValue
     * @returns decode mobx-zod-form value to DOM.value (passed to DOM element)
     */
    rawValueDecoder?: (rawValue: unknown) => string;
    /**
     * @param rawValue
     * @returns encode DOM.value to mobx-zod-form (managing onChange)
     */
    valueEncoder?: (value: string) => string;
  }
}

export namespace f {
  let formTypeIdx = 1;

  const markDef = (t: ZodTypeAny, idx: number) => {
    t._def.__formTypeIdx = idx;
  };

  const createMarkedType = <T extends ZodTypeAny>(type: T) => {
    const currentIdx = formTypeIdx++;

    markDef(unwrapZodType(type), currentIdx);

    return {
      type: () => type,
      is(t: ZodTypeAny) {
        return (unwrapZodType(t)._def as any).__formTypeIdx === currentIdx;
      },
    };
  };

  const dateStringT = createMarkedType(
    z
      .string()
      .trim()
      .regex(DATE_REGEX, `${dayjs().format('YYYY/MM/DD')}のように有効な日付を入力してください。`)
      .formMeta({
        valueEncoder(value) {
          return value.replace(/\//g, '-');
        },
        rawValueDecoder(rawValue) {
          if (typeof rawValue === 'string') {
            return rawValue.replace(/-/g, '/');
          } else if (rawValue != null) {
            return String(rawValue);
          } else {
            return '';
          }
        },
      }),
  );

  export const dateString = dateStringT.type;

  export const isDateString = dateStringT.is;

  // Matches a ss:ss input
  export const timeStringT = createMarkedType(
    z
      .string()
      .trim()
      .regex(/^[0-9]{2}:[0-9]{2}$/),
  );

  export const timeString = timeStringT.type;

  export const isTimeString = timeStringT.is;

  const yenT = createMarkedType(
    z.coerce
      .number()
      .int(`整数で入力してください。`)
      .formMeta({
        rawValueDecoder(rawValue) {
          if (rawValue == null || (typeof rawValue === 'string' && rawValue.trim() === '')) {
            return '¥';
          } else if (!Number.isNaN(Number(rawValue))) {
            // Avoid '111.' is erased '.' because it is formatted to `111`.
            if (typeof rawValue === 'string' && rawValue.endsWith('.')) {
              return moneyStrings.formatYenFront(Number(rawValue)) + '.';
            }
            return moneyStrings.formatYenFront(Number(rawValue));
          } else {
            return String(rawValue);
          }
        },
        valueEncoder(value) {
          if (value.startsWith('¥')) {
            return value.slice(1).replaceAll(',', '');
          }
          return value;
        },
      }),
  );

  export const yen = yenT.type;

  export const isYen = yenT.is;

  export const instanceOfBlobT = createMarkedType(z.instanceof(Blob));

  export const instanceOfBlob = instanceOfBlobT.type;

  export const isInstanceOfBlob = instanceOfBlobT.is;

  export const decimalT = createMarkedType(
    z
      .string()
      .refine(
        (x) => String(x).trim().length > 0 && !Number.isNaN(Number(x)),
        `半角数字で入力してください。`,
      ),
  );

  export const decimal = decimalT.type;

  export const withPhoneRegex = (t: z.ZodString) => {
    return t.regex(
      // @see https://regex101.com/r/wZ4uU6/1
      regexOrEmpty(/\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}/),
      '有効な電話番号で入力してください。',
    );
  };

  export const withEnglishRegex = (t: z.ZodString) => {
    return t.regex(/^[^一-龯ぁ-んァ-ン！：／$]*$/, '英字で入力してください。');
  };

  export const withJapaneseZipcodeRegex = (t: z.ZodString) => {
    return t.regex(
      regexOrEmpty(/^[0-9]{7}$/),
      '半角数字で７文字の郵便番号を入力してください。例：6008216。',
    );
  };

  export const withChineseZipcodeRegex = (t: z.ZodString) => {
    return t.regex(
      regexOrEmpty(/^[0-9]{6}$/),
      '半角数字で６文字の郵便番号を入力してください。例：056001。',
    );
  };

  export const secretT = createMarkedType(z.string());

  export const secret = secretT.type;

  export const isSecret = secretT.is;
}

export const getFieldLabel = (field: MobxZodField<ZodTypeAny>) => {
  return field.type.getFormMeta().label;
};
